// Single typed fetch wrapper for the write API. Always attaches the rw_token as a
// Bearer header (even though we're same-origin in prod, the Lambda still checks the
// header rather than the cookie). On 401, redirect to dash.

import { getToken, redirectToLogin } from './auth.js';

export class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const token = getToken();
  if (!token) redirectToLogin();

  const res = await fetch(path, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: 'include',
  });

  if (res.status === 401) {
    redirectToLogin();
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new ApiError(res.status, text || res.statusText);
  }

  // 204 No Content
  if (res.status === 204) return undefined as T;

  return (await res.json()) as T;
}

export const api = {
  // Reads
  getTree: () => request<TreeJsonResponse>('GET', '/api/tree'),
  getPage: (id: string) => request<{ page: PageResponse }>('GET', `/api/pages/${id}`),

  // Writes
  createPage: (input: CreatePageInput) =>
    request<PageMutationResponse>('POST', '/api/pages', input),
  updatePage: (id: string, input: UpdatePageInput) =>
    request<PageMutationResponse>('PUT', `/api/pages/${id}`, input),
  movePage: (id: string, input: MovePageInput) =>
    request<PageMutationResponse>('PATCH', `/api/pages/${id}/move`, input),
  deletePage: (id: string) =>
    request<{ deleted: boolean; orphanedChildren: number }>('DELETE', `/api/pages/${id}`),

  // Media
  presignUpload: (filename: string, contentType: string) =>
    request<{ uploadUrl: string; publicPath: string; key: string }>(
      'POST',
      '/api/upload',
      { filename, contentType },
    ),
};

// ─── Response types ───

export interface TreeJsonResponse {
  version: string;
  generatedAt: string;
  pages: TreeNodeResponse[];
}

export interface TreeNodeResponse {
  id: string;
  title: string;
  icon: string | null;
  parent: string | null;
  sortOrder: number;
  v: string;
}

export interface PageResponse {
  id: string;
  parentId: string | null;
  sortOrder: number;
  title: string;
  icon: string | null;
  cover: string | null;
  version: string;
  content: unknown;
  contentS3Key: string | null;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface PageMutationResponse {
  id: string;
  version: string;
  page: PageResponse;
  /**
   * Present only on createPage responses where a parent body was supplied.
   * Echoes the parent's post-update state so the caller can rehydrate its
   * cached parent record (new version, content, etc).
   */
  parent?: PageResponse;
}

export interface CreatePageInput {
  title: string;
  /**
   * Live API requires this (non-null) — root-page creation isn't allowed via
   * /api/pages. Typed as nullable so the sandbox mockApi can still create root
   * pages locally without diverging signatures.
   */
  parentId: string | null;
  /**
   * Client-generated ULID. The live API uses it to write the new page row and
   * the parent body (which references this id) in a single DDB transaction.
   * Sandbox/mock callers can omit it; they generate their own internally.
   */
  id?: string;
  /**
   * The parent's updated body with a pageLink to the new page inserted.
   * Required by the live API whenever parentId is non-null; sandbox-only callers
   * can omit it.
   */
  parentBody?: unknown;
  content?: unknown;
  icon?: string | null;
}

export interface UpdatePageInput {
  title?: string;
  content?: unknown;
  icon?: string | null;
  cover?: string | null;
}

export interface MovePageInput {
  parentId: string | null;
  sortOrder?: number;
}
